/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, cloneElement } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Paper,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  DragIndicator,
  Visibility,
  Image,
} from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  getAllAboutImages,
  deleteAboutImage,
  reorderAboutImages,
} from "actions";
import { clearError } from "features/aboutImagesSlice";
import { ADMIN_TEXTS } from "constants/adminTexts";
import { PATHNAME } from "constants/pathnames";
import { FILE_TYPE } from "constants/fileTypes";
import "./AboutImages.scss";
import { BrandButton } from "components/brand";

const imageBase = process.env.REACT_APP_API_IMAGE_URL;

const SortableItem = ({ id, children }) => {
  const {
    setNodeRef,
    transform,
    transition,
    isDragging,
    attributes,
    listeners,
  } = useSortable({
    id,
    data: {
      type: "about-image",
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1400 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {cloneElement(children, { dragHandleProps: listeners })}
    </div>
  );
};

const AboutImageCard = ({
  aboutImage,
  onView,
  onEdit,
  onDelete,
  dragHandleProps,
}) => {
  const getImageUrl = (image) => {
    if (!image) return null;
    return `${imageBase}${image.path}`;
  };

  return (
    <Card className="about-image-card">
      <Box className="card-body">
        <Box className="image-container">
          {aboutImage.image ? (
            aboutImage?.image?.type === FILE_TYPE.VIDEO ? (
              <video
                src={getImageUrl(aboutImage.image)}
                className="image-preview"
                controls
              />
            ) : (
              <img
                src={getImageUrl(aboutImage.image)}
                alt="About"
                className="image-preview"
              />
            )
          ) : (
            <Box className="no-image-placeholder">
              <Image className="no-image-icon" />
              <Typography variant="body2" color="text.secondary">
                {ADMIN_TEXTS.aboutImages.noImage}
              </Typography>
            </Box>
          )}

          <Box className="action-buttons">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onView(aboutImage.id);
              }}
              className="action-button view"
            >
              <Visibility fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(aboutImage.id);
              }}
              className="action-button edit"
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(aboutImage.id);
              }}
              className="action-button delete"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <CardContent className="card-content">
          <Box className="drag-handle" {...dragHandleProps}>
            <DragIndicator className="drag-icon" />
            <Typography variant="body2" color="text.secondary">
              {ADMIN_TEXTS.common.order}: {aboutImage.order}
            </Typography>
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            className="created-date"
          >
            {ADMIN_TEXTS.common.created}:{" "}
            {new Date(aboutImage.createdAt).toLocaleDateString()}
          </Typography>
        </CardContent>
      </Box>
    </Card>
  );
};

export const AboutImagesList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { aboutImages, loading, error } = useSelector(
    (state) => state.aboutImages,
  );

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
  );

  useEffect(() => {
    dispatch(getAllAboutImages());
  }, []);

  useEffect(() => {
    if (error) {
      dispatch(clearError());
    }
  }, [error]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = aboutImages.findIndex((item) => item.id === active.id);
      const newIndex = aboutImages.findIndex((item) => item.id === over.id);

      const newOrder = arrayMove(aboutImages, oldIndex, newIndex);
      const aboutImageIds = newOrder.map((item) => item.id);

      dispatch(reorderAboutImages(aboutImageIds));
    }
  };

  const handleView = (id) => {
    navigate(`${PATHNAME.aboutImages}/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`${PATHNAME.aboutImages}/${id}/edit`);
  };

  const handleDeleteClick = (id) => {
    setDeleteDialog({
      open: true,
      id,
    });
  };

  const handleDelete = () => {
    dispatch(deleteAboutImage(deleteDialog.id));
    setDeleteDialog({
      open: false,
      id: null,
    });
  };

  if (loading) {
    return (
      <Box className="loading-container">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="about-images-list">
      <Box className="list-header">
        <Typography variant="h4" component="h1" fontWeight="bold">
          {ADMIN_TEXTS.aboutImages.aboutImages}
        </Typography>
        <BrandButton
          variant="primary"
          startIcon={<Add />}
          onClick={() => navigate(`${PATHNAME.aboutImages}/create`)}
          className="create-button"
        >
          {ADMIN_TEXTS.aboutImages.addImage}
        </BrandButton>
      </Box>

      {error && (
        <Alert severity="error" className="error-alert">
          {error}
        </Alert>
      )}

      {aboutImages.length === 0 ? (
        <Paper className="empty-state">
          <Image className="empty-icon" />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {ADMIN_TEXTS.aboutImages.noImages}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            className="empty-description"
          >
            {ADMIN_TEXTS.aboutImages.noImagesDescription}
          </Typography>
          <BrandButton
            variant="primary"
            startIcon={<Add />}
            onClick={() => navigate(`${PATHNAME.aboutImages}/create`)}
            className="create-first-button"
          >
            {ADMIN_TEXTS.aboutImages.addFirstImage}
          </BrandButton>
        </Paper>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={aboutImages.map((item) => item.id)}
            strategy={rectSortingStrategy}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 3,
                "@media (min-width: 769px)": {
                  gridTemplateColumns: "repeat(2, 1fr)",
                },
                "@media (min-width: 900px)": {
                  gridTemplateColumns: "repeat(3, 1fr)",
                },
                "@media (min-width: 1200px)": {
                  gridTemplateColumns: "repeat(4, 1fr)",
                },
              }}
            >
              {aboutImages.map((aboutImage) => (
                <SortableItem id={aboutImage.id} key={aboutImage.id}>
                  <AboutImageCard
                    aboutImage={aboutImage}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                  />
                </SortableItem>
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      )}

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>{ADMIN_TEXTS.aboutImages.deleteImage}</DialogTitle>
        <DialogContent>
          <Typography>{ADMIN_TEXTS.aboutImages.confirmDelete}</Typography>
        </DialogContent>
        <DialogActions>
          <BrandButton
            variant="secondary"
            onClick={() => setDeleteDialog({ open: false, id: null })}
            color="primary"
          >
            {ADMIN_TEXTS.common.cancel}
          </BrandButton>
          <BrandButton
            onClick={handleDelete}
            color="error"
            variant="primary"
            className="delete-button"
          >
            {ADMIN_TEXTS.common.delete}
          </BrandButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
